import { api } from '@web/api/client'
import type {
  AppDefinition,
  BridgeConfig,
  OnePagerLaunchShare,
  SessionConfig,
  SessionRecord,
} from '@web/api/types'
import { ChronicleStartup } from '@web/components/ChronicleStartup'
import { PendingSessionView } from '@web/components/PendingSessionView'
import {
  initialLaunchSettings,
  LaunchPills,
  launchSelection,
  launchSettings,
  ProviderSettings,
  usesWorkspace,
} from '@web/components/ProviderSettings'
import { SendButton } from '@web/components/SendButton'
import { SessionCost } from '@web/components/SessionCost'
import { Icon } from '@web/components/ui/icons'
import { appIcon } from '@web/lib/appIcon'
import { LEADGEN_BRIEF } from '@web/lib/demoBriefs'
import { appConfiguration } from '@web/state/app-configuration'
import { useDemoScenarios } from '@web/state/demo-scenarios'
import { handleComposerEnter } from '@web/state/enter-key-behavior'
import { useIdentity } from '@web/state/identity'
import { useEnterKeyBehavior } from '@web/state/local-enter-key-behavior'
import { getSessionStore } from '@web/state/session-store'
import { type RefObject, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const HOST_SETTINGS = new Set([
  'app_id',
  'app_version',
  'app_state_schema_version',
  'app_configuration',
  'app_binding_id',
  'package_digest',
  'protocol',
  'source_session_id',
  'acp_version',
  'provider',
  'execution_mode',
  'environment',
  'provider_session_id',
  'acp_mcp_servers',
  'system_prompt_append',
])

const NEW_SESSION_DESCRIPTIONS: Record<string, string> = {
  'deal-explorer':
    'Research companies, compare evidence, and review qualification and strategy.',
  'lender-coverage':
    'Map lender roles and software focus, review sourced contacts, and track introduction attempts.',
  'coverage-desk':
    'Coordinate relationship coverage, prepare drafts, and track reviews and policy holds.',
  'diligence-room':
    'Organize diligence requests, trace findings to source material, and track open gaps.',
  'sponsor-coverage':
    'Track sourced sponsor holdings and partner coverage, and prepare drafts for review.',
}

function editableSettings(
  settings: Partial<SessionConfig>,
): Partial<SessionConfig> {
  return Object.fromEntries(
    Object.entries(launchSettings(settings)).filter(
      ([key]) => !HOST_SETTINGS.has(key),
    ),
  )
}

type StartupProps = {
  app: AppDefinition
  config: BridgeConfig
  record?: SessionRecord
  onCreated: (record: SessionRecord) => void
  onStarting?: (record: SessionRecord, start: () => Promise<void>) => void
  onChanged: () => void
  startupKey?: string
  pendingHost?: RefObject<HTMLElement | null>
  narrow?: boolean
}

function launchShareDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
}

export function AppStartup(props: StartupProps) {
  if (
    props.app.id === 'agent-builder' ||
    props.app.id === 'agent-simulator' ||
    props.app.id === 'agent-tester' ||
    props.app.id.startsWith('user-app-')
  )
    return <BuilderStartup {...props} />
  return props.app.id === 'chronicle' ? (
    <ChronicleStartup {...props} />
  ) : (
    <StandardAppStartup {...props} />
  )
}

function BuilderStartup({
  app,
  config,
  record,
  onCreated,
  onChanged,
  pendingHost,
  narrow = false,
}: StartupProps) {
  const identity = useIdentity()
  const behavior = useEnterKeyBehavior(
    identity.state?.current_user?.enter_key_behavior,
  )
  const savedApp = app.id.startsWith('user-app-')
  const [prompt, setPrompt] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [showSession, setShowSession] = useState(false)
  const [followup, setFollowup] = useState('')
  const followupDraft = useRef('')
  const pendingRoot = useRef<HTMLDivElement>(null)
  const active = useRef(true)
  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])
  const [settings, setSettings] = useState<Partial<SessionConfig>>(() =>
    initialLaunchSettings(config, {
      provider: 'claude',
      model: 'haiku',
      effort: 'low',
      execution_mode: 'tool-restricted',
    }),
  )
  const briefs = app.id === 'agent-builder' && !savedApp && !record
  const submit = async () => {
    if (busy) return
    setBusy(true)
    setShowSession(true)
    setError('')
    try {
      const session = record
        ? await api.resumeSession(record.id)
        : (
            await api.launchApp(app.id, {
              prompt: prompt.trim(),
              draft: false,
              config:
                app.id === 'agent-builder'
                  ? launchSettings(settings)
                  : {
                      provider: 'claude',
                      model: 'haiku',
                      effort: 'low',
                      execution_mode: 'tool-restricted',
                    },
            })
          ).session
      if (!session) throw new Error('The agent could not start its session.')
      const store = getSessionStore(session.id, session)
      if (!record && prompt.trim())
        store.seedTitleMessage(`initial:${session.id}`, prompt.trim())
      if (!record && prompt.trim())
        void api
          .generateSessionTitle(session.id, `initial:${session.id}`, [
            prompt.trim(),
          ])
          .catch(() => undefined)
      store.saveComposerDraft({
        ...store.getComposerDraft(),
        text: followupDraft.current,
      })
      if (active.current) {
        onCreated(session)
        onChanged()
        requestAnimationFrame(() =>
          window.dispatchEvent(
            new CustomEvent('bridge:focus-composer', {
              detail: { sessionId: session.id },
            }),
          ),
        )
      }
    } catch (failure) {
      setError((failure as Error).message)
      setBusy(false)
    }
  }
  if (showSession) {
    const pending = (
      <PendingSessionView
        app={app}
        root={pendingRoot}
        modal={false}
        label={`Start ${app.title}`}
        title={prompt.trim().split('\n')[0]?.slice(0, 100) || app.title}
        agentName={
          app.id === 'agent-builder'
            ? (launchSelection(config, settings).provider?.title ?? 'Claude')
            : 'Claude'
        }
        model={app.id === 'agent-builder' ? settings.model : 'haiku'}
        narrow={narrow}
        prompt={prompt.trim()}
        filenames={[]}
        draft={followup}
        onDraftChange={(value) => {
          followupDraft.current = value
          setFollowup(value)
        }}
        status="Preparing session…"
        error={error}
        onRetry={() => void submit()}
        onEdit={() => {
          setShowSession(false)
          setError('')
        }}
      />
    )
    return pendingHost?.current
      ? createPortal(pending, pendingHost.current)
      : pending
  }
  return (
    <main className="new-session-pane">
      <form
        className="new-session-launcher w-full max-w-2xl"
        aria-label={`Start ${app.title}`}
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        <div className="new-session-heading">
          <h1>
            {savedApp || record ? app.title : 'What should your agent do?'}
          </h1>
        </div>
        <div className="new-session-composer card relative">
          {!savedApp && !record ? (
            <textarea
              className="min-h-32 w-full resize-none bg-transparent px-4 py-3 text-[15px] outline-none placeholder:text-faint"
              aria-label="Initial agent request"
              autoFocus
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              onKeyDown={(event) =>
                handleComposerEnter(event, behavior, setPrompt, () => {
                  if (prompt.trim()) void submit()
                })
              }
              placeholder="Describe the agent you want to build…"
              rows={4}
              required
              maxLength={6000}
            />
          ) : (
            <p className="px-4 py-3 text-sm text-muted">
              {record
                ? 'Continue your existing session.'
                : 'Launch this saved agent in Agent Simulator.'}
            </p>
          )}
          <div className="new-session-actions border-t border-line px-2 py-2">
            {briefs && (
              <LaunchPills
                config={config}
                settings={settings}
                onChange={(patch) =>
                  setSettings((current) => ({ ...current, ...patch }))
                }
              />
            )}
            <span className="flex-1" />
            <SendButton
              onSend={() => void submit()}
              className="btn btn-primary h-9 w-9 justify-center rounded-full px-0"
              disabled={!record && !savedApp && !prompt.trim()}
              label={
                record
                  ? 'Resume session'
                  : savedApp
                    ? 'Launch agent'
                    : 'Start session'
              }
            >
              <Icon name="send" size={16} />
            </SendButton>
          </div>
        </div>
        {briefs && (
          <button
            type="button"
            className="new-session-canned-brief btn btn-sm"
            onClick={() => setPrompt(LEADGEN_BRIEF)}
          >
            <Icon name="magic" size={13} />
            Use the lead-gen brief
          </button>
        )}
        {briefs && (
          <details className="px-4 py-2 text-sm">
            <summary>Advanced settings</summary>
            <ProviderSettings
              config={config}
              settings={settings}
              onChange={(patch) =>
                setSettings((current) => ({ ...current, ...patch }))
              }
            />
            <p className="text-muted">
              The lead-gen brief always uses a lightweight, tool-restricted
              session.
            </p>
          </details>
        )}
        {error && (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        )}
      </form>
    </main>
  )
}

function StandardAppStartup({
  app,
  config,
  record,
  onCreated,
  onChanged,
}: StartupProps) {
  const startup = app.descriptor?.startup
  const configuration = appConfiguration(app, record)
  const uiOnly = configuration.ui.mode === 'ui'
  const demo = !!configuration.demo
  const scenarios = useDemoScenarios(
    app.id,
    record?.config.package_digest ?? app.digest,
    demo,
  )
  const steps = startup?.steps?.length
    ? startup.steps
    : [
        {
          id: 'confirm',
          title: `Start a ${app.title} session`,
          description:
            NEW_SESSION_DESCRIPTIONS[app.id] ??
            'Create a new session with the recommended settings.',
          fields: [],
        },
      ]
  const [step, setStep] = useState(record?.config.startup_step ?? 0)
  const [draft, setDraft] = useState(record)
  const [settings, setSettings] = useState<Partial<SessionConfig>>(
    () =>
      record?.config ??
      initialLaunchSettings(config, {
        ...config.defaults,
        ...startup?.defaults,
        ...(app.configuration?.session_defaults as Partial<SessionConfig>),
        permission_mode: config.defaults.permission_mode,
        ...(demo
          ? {
              provider: 'claude' as const,
              model: 'haiku',
              effort: 'low',
              execution_mode: 'tool-restricted' as const,
            }
          : {}),
      }),
  )
  const [values, setValues] = useState<Record<string, unknown>>(() => ({
    ...Object.fromEntries(
      steps
        .flatMap((item) => item.fields ?? [])
        .filter(
          (field) =>
            field.default != null ||
            field.type === 'checkbox' ||
            field.type === 'text' ||
            field.type === 'textarea',
        )
        .map((field) => [
          field.key,
          field.default ?? (field.type === 'checkbox' ? false : ''),
        ]),
    ),
    ...(app.configuration?.setup_defaults as Record<string, unknown>),
    ...record?.config.startup_values,
  }))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const showOnePagerShares = app.id === 'onepager' && !record
  const [launcherShares, setLauncherShares] = useState<OnePagerLaunchShare[]>(
    [],
  )
  const [sharesLoading, setSharesLoading] = useState(showOnePagerShares)
  const [sharesError, setSharesError] = useState('')
  const [shareQuery, setShareQuery] = useState('')
  const [shareReload, setShareReload] = useState(0)
  const [launchingSlug, setLaunchingSlug] = useState<string | null>(null)
  const launchPending = useRef(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: incrementing shareReload retries the list request.
  useEffect(() => {
    if (!showOnePagerShares) return
    let active = true
    setSharesLoading(true)
    setSharesError('')
    void api
      .onepagerLaunchShares()
      .then(({ shares }) => {
        if (active) setLauncherShares(shares)
      })
      .catch((failure: Error) => {
        if (active) setSharesError(failure.message)
      })
      .finally(() => {
        if (active) setSharesLoading(false)
      })
    return () => {
      active = false
    }
  }, [showOnePagerShares, shareReload])
  const visibleShares = launcherShares.filter((share) => {
    const query = shareQuery.trim().toLowerCase()
    return (
      !query ||
      share.title.toLowerCase().includes(query) ||
      share.slug.toLowerCase().includes(query)
    )
  })
  const selection = launchSelection(config, settings)
  const workspace = usesWorkspace(selection.mode?.id)
  const current = steps[Math.min(step, steps.length - 1)]!
  const last = step >= steps.length - 1
  const startupValues = demo
    ? {
        ...values,
        scenario_id:
          values.scenario_id ??
          scenarios.catalog?.default_scenario ??
          configuration.demo?.default_scenario,
      }
    : values
  const patch = (value: Partial<SessionConfig>) =>
    setSettings((previous) => ({ ...previous, ...value }))
  const submit = async (shareSlug?: string) => {
    if (launchPending.current || (!uiOnly && !selection.available)) return
    launchPending.current = true
    setBusy(true)
    setLaunchingSlug(shareSlug ?? null)
    setError('')
    const launchValues = shareSlug
      ? { ...startupValues, share_slug: shareSlug }
      : startupValues
    try {
      let session = draft
      if (!session) {
        const launched = await api.launchApp(app.id, {
          packageDigest: app.digest,
          draft: true,
          config: {
            ...editableSettings(
              launchSettings({
                ...settings,
                execution_mode: selection.mode?.id,
              }),
            ),
            ...(!uiOnly
              ? {
                  protocol: 'acp' as const,
                  acp_version: 2 as const,
                  provider: selection.provider?.id,
                  execution_mode: selection.mode?.id,
                  environment: selection.environment,
                }
              : {}),
            startup_values: launchValues,
            startup_step: 0,
          },
        })
        if (!launched.session)
          throw new Error('This agent could not create a session.')
        session = launched.session
        setDraft(session)
      }
      if (current.action && session.config.app_binding_id) {
        const fields = Object.fromEntries(
          (current.fields ?? []).map((field) => [field.key, values[field.key]]),
        )
        await api.configureApp(session.config.app_binding_id, current.action, {
          ...current.actionArgs,
          ...fields,
        })
      }
      session = await api.updateConfig(session.id, {
        ...editableSettings(settings),
        startup_values: launchValues,
        startup_step: step + 1,
      })
      if (last) session = await api.startSession(session.id)
      setStep(step + 1)
      setDraft(session)
      onCreated(session)
      onChanged()
    } catch (failure) {
      setError((failure as Error).message)
    } finally {
      launchPending.current = false
      setBusy(false)
      setLaunchingSlug(null)
    }
  }
  const modelControls = (
    <ProviderSettings
      config={config}
      settings={settings}
      onChange={patch}
      disabled={busy}
      providerLocked={demo || !!draft}
    />
  )
  return (
    <div className="startup-surface">
      <section
        className={`app-startup${showOnePagerShares ? ' onepager-startup' : ''}`}
      >
        <div className="startup-brand">
          <img
            src={appIcon(app.iconUrl, 128)}
            width={48}
            height={48}
            decoding="async"
            alt=""
          />
          <span>{app.title}</span>
        </div>
        <form
          className="startup-card"
          onSubmit={(event) => {
            event.preventDefault()
            void submit()
          }}
        >
          {!showOnePagerShares && (
            <p className="label">
              New session
              {steps.length > 1
                ? ` · Step ${Math.min(step + 1, steps.length)} of ${steps.length}`
                : ''}
            </p>
          )}
          <h1>{current.title}</h1>
          <p className="startup-description">{current.description}</p>
          {showOnePagerShares && (
            <p className="onepager-startup-model">
              {selection.provider?.title ?? 'Claude'} ·{' '}
              {settings.model ?? 'Default model'} ·{' '}
              {settings.effort ?? 'Default effort'} effort
            </p>
          )}
          <div className="startup-fields">
            {app.launchMode === 'chat' && !demo && workspace && (
              <label>
                Project
                <input
                  aria-label="Project"
                  placeholder="Blank workspace, or a project path"
                  value={settings.project ?? ''}
                  onChange={(event) => patch({ project: event.target.value })}
                />
              </label>
            )}
            {(current.fields ?? [])
              .filter((field) => !demo || field.key !== 'scenario_id')
              .map((field) => (
                <label key={field.key}>
                  {field.label}
                  {field.type === 'select' ? (
                    <select
                      required={field.required}
                      aria-label={field.label}
                      value={String(values[field.key] ?? '')}
                      onChange={(event) =>
                        setValues({
                          ...values,
                          [field.key]: event.target.value,
                        })
                      }
                    >
                      <option value="" disabled={field.required}>
                        Choose…
                      </option>
                      {field.options?.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : field.type === 'textarea' ? (
                    <textarea
                      aria-label={field.label}
                      required={field.required}
                      placeholder={field.placeholder}
                      value={String(values[field.key] ?? '')}
                      onChange={(event) =>
                        setValues({
                          ...values,
                          [field.key]: event.target.value,
                        })
                      }
                    />
                  ) : field.type === 'checkbox' ? (
                    <input
                      aria-label={field.label}
                      type="checkbox"
                      checked={values[field.key] === true}
                      onChange={(event) =>
                        setValues({
                          ...values,
                          [field.key]: event.target.checked,
                        })
                      }
                    />
                  ) : (
                    <input
                      aria-label={field.label}
                      type={field.type === 'number' ? 'number' : 'text'}
                      step={field.type === 'number' ? 'any' : undefined}
                      required={field.required}
                      placeholder={field.placeholder}
                      value={String(values[field.key] ?? '')}
                      onChange={(event) =>
                        setValues({
                          ...values,
                          [field.key]:
                            field.type === 'number'
                              ? event.target.value === ''
                                ? undefined
                                : Number(event.target.value)
                              : event.target.value,
                        })
                      }
                    />
                  )}
                  {field.description && <small>{field.description}</small>}
                </label>
              ))}
          </div>
          {!uiOnly && startup?.modelSettings === 'primary' && modelControls}
          <details className="startup-advanced">
            <summary>Advanced settings</summary>
            {demo && (
              <div className="startup-fields">
                <label>
                  Scenario
                  <select
                    aria-label="Scenario"
                    disabled={busy || !scenarios.catalog}
                    value={String(startupValues.scenario_id ?? '')}
                    onChange={(event) =>
                      setValues({
                        ...values,
                        scenario_id: event.target.value,
                      })
                    }
                  >
                    {!scenarios.catalog && (
                      <option value="">Loading scenarios…</option>
                    )}
                    {scenarios.catalog?.scenarios.map((scenario) => (
                      <option key={scenario.id} value={scenario.id}>
                        {scenario.title}
                      </option>
                    ))}
                  </select>
                  <small>
                    {
                      scenarios.catalog?.scenarios.find(
                        (scenario) => scenario.id === startupValues.scenario_id,
                      )?.description
                    }{' '}
                    You can change the scenario during this session.
                  </small>
                </label>
                {scenarios.error && (
                  <p role="alert" className="text-danger text-sm">
                    {scenarios.error}{' '}
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={scenarios.retry}
                    >
                      Retry
                    </button>
                  </p>
                )}
              </div>
            )}
            {!uiOnly && startup?.modelSettings !== 'primary' && modelControls}
            <div className="startup-fields">
              <label>
                Sleep after inactivity (minutes)
                <input
                  aria-label="Sleep after inactivity (minutes)"
                  type="number"
                  min={1}
                  max={1440}
                  required
                  value={
                    settings.idle_timeout_minutes ??
                    configuration.sessions.idle_timeout_minutes
                  }
                  onChange={(event) =>
                    patch({
                      idle_timeout_minutes: Number(event.target.value),
                    })
                  }
                />
              </label>
              {!!configuration.schedules?.length && (
                <label>
                  Schedule
                  <select
                    aria-label="Schedule"
                    value={settings.schedule_id ?? ''}
                    onChange={(event) =>
                      patch({ schedule_id: event.target.value || null })
                    }
                  >
                    <option value="">No schedule</option>
                    {configuration.schedules.map((schedule) => (
                      <option key={schedule.id} value={schedule.id}>
                        {schedule.id} · {schedule.expression} (
                        {schedule.timezone})
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          </details>
          {(error || scenarios.error) && (
            <p className="text-danger text-sm" role="alert">
              {error ||
                'Scenarios could not be loaded. Open Advanced settings to retry.'}
            </p>
          )}
          <div className="startup-footer">
            <span>
              {draft
                ? 'Session saved. Continue whenever you’re ready.'
                : 'Your session is saved after this step.'}
            </span>
            <button
              className="btn btn-primary"
              type="submit"
              disabled={
                busy ||
                (!uiOnly && !selection.available) ||
                (demo && !scenarios.catalog)
              }
            >
              {busy ? (
                <>
                  <Icon name="loader" size={14} className="spin" />
                  Saving…
                </>
              ) : last ? (
                showOnePagerShares ? (
                  'Start with all shares'
                ) : (
                  'Start session'
                )
              ) : (
                'Continue'
              )}
            </button>
          </div>
        </form>
        {showOnePagerShares && (
          <aside
            className="onepager-startup-shares"
            aria-label="Existing shares"
          >
            <div className="onepager-startup-shares-heading">
              <div>
                <h2>Shares</h2>
                <p>Select a share to open it in a new session.</p>
              </div>
              {!sharesLoading && !sharesError && (
                <span>{launcherShares.length}</span>
              )}
            </div>
            <label className="onepager-startup-search">
              <span className="sr-only">Search shares</span>
              <input
                type="search"
                value={shareQuery}
                onChange={(event) => setShareQuery(event.target.value)}
                placeholder="Search shares"
                aria-label="Search shares"
              />
            </label>
            {sharesLoading ? (
              <p className="onepager-startup-list-message" role="status">
                Loading shares…
              </p>
            ) : sharesError ? (
              <p className="onepager-startup-list-message" role="alert">
                {sharesError}{' '}
                <button
                  type="button"
                  onClick={() => setShareReload((count) => count + 1)}
                >
                  Retry
                </button>
              </p>
            ) : visibleShares.length ? (
              <>
                <div className="onepager-startup-list-head" aria-hidden="true">
                  <span>Share</span>
                  <span>Status</span>
                  <span>Created</span>
                </div>
                <ul className="onepager-startup-list">
                  {visibleShares.map((share) => (
                    <li key={share.slug}>
                      <button
                        type="button"
                        className="onepager-startup-share"
                        onClick={() => void submit(share.slug)}
                        disabled={busy || !selection.available}
                        aria-label={`Open ${share.title} in a new session`}
                      >
                        <span className="onepager-startup-share-name">
                          <strong>{share.title}</strong>
                          <small>/{share.slug}</small>
                        </span>
                        <span className="onepager-startup-share-status">
                          {launchingSlug === share.slug
                            ? 'Opening…'
                            : share.published_commit
                              ? 'Published'
                              : 'Draft'}
                        </span>
                        <time dateTime={share.created_at}>
                          {launchShareDate(share.created_at)}
                        </time>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="onepager-startup-list-message">
                {shareQuery ? 'No matching shares.' : 'No shares yet.'}
              </p>
            )}
          </aside>
        )}
      </section>
      <footer
        className="session-status chrome"
        role="contentinfo"
        aria-label="Session status bar"
      >
        <span className="session-lifecycle">Setup</span>
        <SessionCost
          sessionId={draft?.id}
          total={draft?.total_cost_usd ?? 0}
          unreported={!draft}
        />
      </footer>
    </div>
  )
}
